import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ValidatedTitleInput } from '../../../components/climb-create/ValidatedTitleInput';
import { RoutesetterPicker, type RoutesetterSelection } from '../../../components/routesetters/RoutesetterPicker';
import { HoldTypeDialog } from '../../../components/climbs/climb-editor/HoldTypeDialog';
import { CLIMB_EXCEPTIONS } from '../../../components/climbs/climb-exceptions';
import type { ClimbScanColor, GradingSystemSummary } from '../../../types/climb-scan';

/** One climb's form state, held by the page so the image editor shares it. */
export interface ScanColorDraft {
  name: string;
  gradeId: string;
  colorName: string;
  routeSetter: RoutesetterSelection | null;
  exceptions: string[];
  description: string;
}

interface ScanClimbFormProps {
  color: ClimbScanColor;
  draft: ScanColorDraft;
  onDraftChange: (patch: Partial<ScanColorDraft>) => void;
  gradingSystem: GradingSystemSummary | null;
  gymSlug: string;
  locationId: string | null;
  fieldErrors: Record<string, string>;
  /** A name drawn from the gym's list, offered when there are any left. */
  onDrawName?: () => void;
  /** A route whose line is not yet two points, and so cannot be drawn or saved. */
  lineIncomplete: boolean;
}

/**
 * Everything a scan's colour needs decided before it is a climb — the same
 * questions `/boulders/new` asks, with the scan's own answers already filled in
 * (the colour it detected, the setters credited on the scan), because the point
 * of a scan is that most of it has been decided already.
 *
 * It lives in the panel beside the photo rather than under its row in the list,
 * so the marks being drawn and the fields being filled are side by side: they
 * are two halves of describing one climb, and neither is much use without
 * seeing the other.
 *
 * The fields only. Saving it, scanning it again and throwing it away are the
 * row above (see `ScanClimbActions`) — they act on the climb rather than
 * describe it, and they belong at the head of the column where they can be
 * reached without scrolling this form to its end.
 */
export function ScanClimbForm({
  color,
  draft,
  onDraftChange,
  gradingSystem,
  gymSlug,
  locationId,
  fieldErrors,
  onDrawName,
  lineIncomplete,
}: ScanClimbFormProps) {
  const { t } = useTranslation();

  function toggleException(value: string) {
    onDraftChange({
      exceptions: draft.exceptions.includes(value)
        ? draft.exceptions.filter((candidate) => candidate !== value)
        : [...draft.exceptions, value],
    });
  }

  return (
    <div className="space-y-4" data-testid="scan-climb-form">
      <ValidatedTitleInput
        title={draft.name}
        locationId={locationId}
        onTitleChange={(name) => onDraftChange({ name })}
        hasError={!!fieldErrors.name}
        errorMessage={fieldErrors.name}
        excludeClimbId={color.climb?.id ?? null}
        // Beside the field it fills, so it reads as "another one of these"
        // rather than as a separate thing to do.
        trailingAction={
          onDrawName && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-9 shrink-0"
              onClick={onDrawName}
              aria-label={t('gymAdmin.scans.drawName')}
              title={t('gymAdmin.scans.drawName')}
            >
              <RefreshCw className="size-4" aria-hidden />
            </Button>
          )
        }
      />

      {/* The whole ladder, laid out rather than folded into a select: a grade is
          picked against the ones either side of it. One at a time — a climb has
          one grade — so an armed chip cannot be turned off, only replaced. */}
      <div className="space-y-2">
        <Label id={`scan-grade-label-${color.id}`}>{t('gymAdmin.scans.grade')}</Label>
        <ToggleGroup
          type="single"
          value={draft.gradeId}
          onValueChange={(gradeId: string) => {
            if (gradeId) onDraftChange({ gradeId });
          }}
          aria-labelledby={`scan-grade-label-${color.id}`}
          className={`flex flex-wrap gap-1.5 rounded-md ${
            fieldErrors.grade_id ? 'border border-red-500 p-1.5' : ''
          }`}
        >
          {(gradingSystem?.grades ?? []).map((grade) => (
            <ToggleGroupItem
              key={grade.id}
              value={grade.id}
              aria-label={grade.name}
              className={`min-w-9 rounded-md border px-2 py-1 text-xs transition-colors ${
                draft.gradeId === grade.id
                  ? 'border-primary bg-primary font-semibold text-primary-foreground'
                  : 'border-input bg-background text-foreground hover:bg-muted'
              }`}
            >
              {grade.name}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {(gradingSystem?.grades ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">{t('gymAdmin.scans.noGrades')}</p>
        )}
        {fieldErrors.grade_id && <p className="text-sm text-red-500">{fieldErrors.grade_id}</p>}
      </div>

      {/* Picked off the gym's list rather than typed: a wall gets named one
          colour at a time here, and a type invented for the first problem has
          to be there for the second — which is why the picker adds it for real.
          Same control as the climb editor's. */}
      <div className="space-y-2">
        <Label>{t('gymAdmin.scans.holdColor')}</Label>
        <HoldTypeDialog
          as="field"
          value={draft.colorName}
          // The scan's own place, and the panel's gym where the scan has not
          // said — the endpoint takes either an id or a slug, and on this
          // surface they are the same place.
          locationId={locationId ?? gymSlug}
          onChange={(colorName) => onDraftChange({ colorName })}
        />
      </div>

      <div className="space-y-2">
        <Label>{t('gymAdmin.scans.routeSetter')}</Label>
        <RoutesetterPicker
          mode="single"
          value={draft.routeSetter ? [draft.routeSetter] : []}
          onChange={(next: RoutesetterSelection[]) => onDraftChange({ routeSetter: next[0] ?? null })}
          locationId={gymSlug}
          placeholder={t('gymAdmin.scans.routeSetterPlaceholder')}
        />
      </div>

      <div className="space-y-2">
        <Label>{t('gymAdmin.scans.exceptions')}</Label>
        <div className="flex flex-wrap gap-2">
          {CLIMB_EXCEPTIONS.map((exception) => {
            const on = draft.exceptions.includes(exception);

            return (
              <button
                key={exception}
                type="button"
                aria-pressed={on}
                onClick={() => toggleException(exception)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background text-foreground hover:bg-muted'
                }`}
              >
                {t(`climbExceptions.${exception}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`scan-description-${color.id}`}>{t('gymAdmin.scans.description')}</Label>
        <Textarea
          id={`scan-description-${color.id}`}
          rows={2}
          value={draft.description}
          onChange={(event) => onDraftChange({ description: event.target.value })}
        />
      </div>

      {fieldErrors.base && <p className="text-sm text-red-500">{fieldErrors.base}</p>}

      {/* A route's line has nowhere else to be asked for: the line editor draws
          on the photo but says nothing about it. A boulder's missing roles are
          not here — they are written on the wall they are answered on (see
          `MissingHint`). */}
      {lineIncomplete && (
        <p className="text-sm text-muted-foreground" data-testid="missing-line-hint">
          {t('gymAdmin.scans.missingLine')}
        </p>
      )}
    </div>
  );
}
